import { apiFetch } from './api.js';

/**
 * Turning the server's symbolic formulas into Excel formulas for a workbook.
 *
 * The API returns expressions written with FIELD NAMES where cell references
 * belong (GET /export/formulas, node/queries/exportFormulas.js). All this does
 * is swap the names for column letters. It never composes arithmetic — the
 * expressions live next to the SQL they mirror, not here.
 */

//Cache per sheet for the life of the page: the spec is static, and an export
//should not wait on a round trip it already made.
const cache = new Map();

export async function fetchSheetFormulas(sheetName) {
    if (cache.has(sheetName)) return cache.get(sheetName);
    try {
        const res = await apiFetch(`/export/formulas?sheet=${encodeURIComponent(sheetName)}`,
            { method: 'GET' });
        if (res.status !== 200) return {};
        const payload = await res.json();
        const formulas = payload?.formulas ?? {};
        cache.set(sheetName, formulas);
        return formulas;
    } catch {
        //A failed probe must not block the download. The export falls back to
        //values, which is exactly what it did before formulas existed.
        return {};
    }
}

/**
 * Build the per-column formula writer for one export.
 *
 * @param formulas  spec from the API: { Field: { expr, inputs } }
 * @param columnOf  Map<fieldName, column letter> for the columns actually present
 * @param redacted  Set of field names hidden from this user (hiddenCostFields)
 * @returns (fieldName, excelRow) => string | null
 */
export function makeFormulaBuilder(formulas, columnOf, redacted) {
    return (fieldName, row) => {
        const spec = formulas?.[fieldName];
        if (!spec) return null;

        //THE GATE. A formula is plain text in the file that reconstructs the
        //cost model, and it would also point at blank cells and produce a
        //confidently wrong number. So it is emitted only when every input is a
        //column that is actually present AND that this user is allowed to see.
        //Otherwise the server's already-redacted value is written instead.
        for (const input of spec.inputs) {
            if (!columnOf.has(input)) return null;
            if (redacted?.has(input)) return null;
        }
        if (redacted?.has(fieldName)) return null;

        //Substitute outside quoted literals only: IF(CommissionCurrency="USD",…)
        //must keep its "USD" even if some sheet ever carries a column by that
        //name. Splitting on the quotes keeps them verbatim.
        //
        //Whole-word matching also means PurchasePrice is never hit inside
        //PurchasePriceUSD, and Excel function names are left alone — they are
        //not in columnOf, so they fall through unchanged.
        return spec.expr
            .split(/("[^"]*")/)
            .map((part, i) => (i % 2 === 1 ? part : part.replace(
                /[A-Za-z_][A-Za-z0-9_]*/g,
                (name) => {
                    const col = columnOf.get(name);
                    return col ? `${col}${row}` : name;
                }
            )))
            .join('');
    };
}
